import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { staffWorkspace, useAccount, useSwitchStaffOrg, type Account, type StaffWorkspace } from "../../../app/session";
import { api } from "../../../data";
import { ErrorState, Loading } from "../../../components/States";
import { PortalLayout, type NavItem } from "../../../components/shell/PortalLayout";
import { PageHead, useAction } from "../../../components/ui";
import { TarifHinweis, useModul } from "../../../components/Tarif";
import { DarstellungAbschnitt, saveThemeChoice, themeNotSavedText, useDarstellungEntwurf } from "../../../components/Darstellung";
import { toast } from "../../../components/Toast";
import { initials } from "../../../lib/format";
import { talentName } from "../../../data/queries";
import {
  createAgency, createAgencyWithData, createCreatorAccount, inviteCreator, loadEinrichtung, loadLifecycle, loadSetupStatus, saveFirma, saveLadder, saveMemberSelf, updateSettings, type EinrichtungData,
} from "./data";
import { BankPanel, BrandingForm, FirmaForm, KonditionenForm, LadderEditor, brandingDraftFrom, firmaDraftFrom, stepDrafts, type Mode } from "./forms";
import {
  AGENCY_COUNTRIES, CREATOR_SEGMENTS, Check, isHiddenText, b2bMissingText, creatorNurUnternehmerText, isCreatorSegment, ONBOARDING_STEPS, countryDefaults, firstOpenStep, isOnboardingStep, listText, missingForStep, nextStep, onboardingSteps,
  sampleLadder, smallBusinessLabel, sortTemplates, stepIndex, textGroup, textLabel, textRank, validateLadder, SETUP_ITEMS, type OnboardingStep, type StepDraft,
} from "./model";
import { DEMO_NOTE, FormErrors, TextField, submitMode } from "./parts";
import { agencyBottom, agencyNav, studioBottom, studioNav } from "../../../app/nav";
import { MAX_PASSWORD_LENGTH, MIN_PASSWORD_LENGTH, passwordHint } from "@talentkontor/domain";
import { t, currentLang, setCreatorKonto, sprachName, ta } from "../../../lib/sprache";
import { steuerVerantwortung } from "../../../model/steuer-verantwortung";
import { fehlerText } from "../../../lib/fehler";
import { hatHerkunft, herkunftAus } from "../../../lib/herkunft";
import { B2bBestaetigung, RechtstexteZustimmung, rememberPendingConsent, settlePendingConsents } from "./Lebenszyklus";
import { merkeEinrichtungNachZahlung } from "./nachZahlung";
import { StartRahmen } from "../../../components/shell/StartRahmen";
import { meldeRegistrierung } from "../../../lib/einwilligung";
import { rememberKauf, AdresseSpaeter } from "./ZahlungsmethodeStart";
import { CaptchaFeld, captchaConfig } from "../../konto/Captcha";
import { KontoVorhanden, PostfachPruefen } from "../../konto/PostfachPruefen";
import { authFehlerArt } from "../../konto/auth";
import { OffeneEinladungen } from "../../creator/OffeneEinladungen";

/**
 * Einrichtung einer neuen Agentur (11_app_agentur/onboarding-*.html) als Schrittfolge unter /einrichtung/<schritt>.
 * Schritt 2 legt die Agentur an (create_organization); fehlende Pflichtangaben meldet org_setup_status.
 */

const NAME_KEY = "tk-einrichtung-name";
export function rememberAgencyName(name: string) {
  try {
    if (name) localStorage.setItem(NAME_KEY, name);
    else localStorage.removeItem(NAME_KEY);
  } catch {
    /* ohne Speicher: Name wird in Schritt 2 erneut eingegeben */
  }
}
function storedAgencyName(): string {
  try {
    return localStorage.getItem(NAME_KEY) ?? "";
  } catch {
    return "";
  }
}

// Tarif aus der Preisseite (?tarif=) und Schnellwahl „Ich führe die Agentur allein“: bis zum Anlegen der Agentur gemerkt
const PLAN_KEY = "tk-einrichtung-tarif";
const SOLO_KEY = "tk-einrichtung-allein";
function remember(key: string, value: string | null) {
  try {
    // localStorage: Tarif und Schnellwahl überstehen den Bestätigungslink, der in einem neuen Tab aufgeht
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {
    /* ohne Speicher: Standard-Testtarif bzw. keine Schnellwahl */
  }
}
function recall(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
/** Tarifschlüssel aus einem Link der Preisseite (nur Kleinbuchstaben, Ziffern, Bindestrich) */
export function trialPlanFromQuery(value: string | null | undefined): string | null {
  const v = (value ?? "").trim().toLowerCase();
  return /^[a-z0-9_-]{2,30}$/.test(v) ? v : null;
}
export function rememberTrialPlan(key: string | null) {
  remember(PLAN_KEY, key);
}
// Art des Kontos aus der Registrierung (?art=creator, 0178): bis zum Anlegen gemerkt, auch über den Bestätigungslink
const ART_KEY = "tk-einrichtung-art";
export function rememberKontoArt(art: "creator" | null) {
  remember(ART_KEY, art);
}
export function storedKontoArt(): "creator" | null {
  return recall(ART_KEY) === "creator" ? "creator" : null;
}
const SEGMENT_KEY = "tk-einrichtung-zielgruppe";
export function rememberZielgruppe(segment: string | null) {
  remember(SEGMENT_KEY, isCreatorSegment(segment) ? segment : null);
}
export function rememberSolo(name: string | null) {
  remember(SOLO_KEY, name);
}
export function storedSolo(): string | null {
  return recall(SOLO_KEY);
}

/** Testphase aus den Produkt-Standards (Dauer, Tarif) und die angebotenen Tarife; ohne Anmeldung leer. */
export function useTrialInfo(enabled = true) {
  return useQuery({
    queryKey: ["platform_defaults", "testphase"],
    enabled,
    queryFn: async () => {
      const [defaults, plans] = await Promise.all([
        api.select<{ key: string; value: unknown }>("platform_defaults", { columns: "key, value", filters: [["key", "in", ["trial_days", "trial_plan"]]] }),
        api.select<{ key: string; name: string; active: boolean }>("plans", { columns: "key, name, active", order: [["sort_order", "asc"]] }),
      ]);
      const days = Number(defaults.find((d) => d.key === "trial_days")?.value ?? NaN);
      const planKey = String(defaults.find((d) => d.key === "trial_plan")?.value ?? "");
      return { days: Number.isFinite(days) ? days : null, planKey, plans: plans.filter((p) => p.active) };
    },
    staleTime: 300_000,
  });
}
export function trialText(info: { days: number | null; planKey: string; plans: { key: string; name: string }[] } | undefined, chosen?: string | null): string {
  const key = chosen && info?.plans.some((p) => p.key === chosen) ? chosen : info?.planKey;
  const plan = info?.plans.find((p) => p.key === key)?.name;
  const days = info?.days ? t(`${info.days} Tage`, `${info.days} days`) : "die";
  return t(`${days} Testphase${plan ? t(` im Tarif ${plan}`, ` on the ${plan} plan`) : ""}`, `${days} trial${plan ? t(` im Tarif ${plan}`, ` on the ${plan} plan`) : ""}`);
}

/** Schlanke Mahnleiter für den Anfang: eine Zahlungserinnerung nach sieben Tagen, ohne Gebühr. */
export function slimLadder(): StepDraft[] {
  return [{ key: "reminder", days: "7", day_type: "calendar", fee: "", interest: false, is_reminder: true }];
}

/** Arbeitsbereich, in dem die angemeldete Person Admin ist (bevorzugt die zuletzt gewählte Agentur). */
function chefWorkspace(account: Account): StaffWorkspace | null {
  const preferred = staffWorkspace(account);
  if (preferred?.isChef) return preferred;
  const chef = account.memberships.find((m) => m.role === "chef_manager");
  return chef ? staffWorkspace(account, chef.org_id) : null;
}

/** K-364: vorgemerkte Zustimmungen (AGB, Datenschutz) eintragen, dann ins Portal */
function WeiterInsPortal() {
  const navigate = useNavigate();
  useEffect(() => {
    void settlePendingConsents().catch(() => undefined).finally(() => navigate("/agentur", { replace: true }));
  }, [navigate]);
  return <Loading />;
}

/** /einrichtung ohne Schritt: zum ersten offenen Schritt. */
export function EinrichtungStart() {
  const { session, account, loading } = useAccount();
  const ws = account ? chefWorkspace(account) : null;
  const setup = useQuery({ queryKey: ["org_setup_status", ws?.org.org_id ?? null], enabled: !!ws, queryFn: () => loadSetupStatus(api, ws!.org.org_id) });
  const life = useQuery({ queryKey: ["org_lifecycle", ws?.org.org_id ?? null], enabled: !!ws, queryFn: () => loadLifecycle(api, ws!.org.org_id), staleTime: 60_000 });
  if (loading || (ws && (setup.isPending || life.isLoading))) return <Loading />;
  if (!session) return <Navigate to="/einrichtung/registrierung" replace />;
  if (ws && life.data?.needs_payment_method) return <Navigate to="/agentur" replace />;
  // Ohne Agentur: Schnellstart (Agentur anlegen, dann Zahlung), Firmendaten erst danach
  if (!ws) return <Navigate to="/einrichtung/registrierung" replace />;
  // Creator-Konto (0178): alles erledigt → ins eigene Büro; Agentur: zuletzt Creator einladen
  if (ws.isCreator) { const open = firstOpenStep(setup.data, true); return <Navigate to={open ? `/einrichtung/${open}` : "/agentur"} replace />; }
  return <Navigate to={`/einrichtung/${firstOpenStep(setup.data) ?? "creator"}`} replace />;
}

export function Einrichtung() {
  const { schritt } = useParams();
  const { session, account, loading, error } = useAccount();
  const ws = account ? chefWorkspace(account) : null;
  // Inhaber 13.10.2026: Firmendaten, Konditionen und Vorlagen gehören nicht zur Registrierung. Solange kein Tarif abgeschlossen
  // (oder die Zahlung bewusst übersprungen) ist, führt jeder Einrichtungsschritt zur Tarifwahl.
  const life = useQuery({ queryKey: ["org_lifecycle", ws?.org.org_id ?? null], enabled: !!ws, queryFn: () => loadLifecycle(api, ws!.org.org_id), staleTime: 60_000 });
  if (!isOnboardingStep(schritt)) return <Navigate to="/einrichtung" replace />;
  if (loading || (ws && life.isLoading)) return <Loading />;
  if (session && (error || !account)) return <ErrorState error={error ?? new Error(t("Konto konnte nicht geladen werden.", "The account could not be loaded."))} />;
  if (ws && life.data?.needs_payment_method) return <Navigate to="/agentur" replace />;
  // K-364: Der Bestätigungslink führt hierher; mit vorhandener Agentur geht es ins Portal statt in den alten Assistenten
  if (ws && schritt === "registrierung") return <WeiterInsPortal />;
  // Ohne Konto bzw. ohne Agentur gibt es nur den ersten Schritt, im schlanken Rahmen ohne Menü und ohne Schrittliste
  if (!ws) {
    if (schritt !== "registrierung") return <Navigate to="/einrichtung/registrierung" replace />;
    return (
      <StartRahmen title={t("Konto anlegen", "Create account")} schritt={1}
        onSignOut={session ? () => { void api.signOut().catch(() => undefined).finally(() => { window.location.assign("/anmelden"); }); } : undefined}>
        <SchrittSeite step="registrierung" ws={null} loggedIn={!!session} schlank />
      </StartRahmen>
    );
  }
  // Schritte, die es im Creator-Konto nicht gibt: zur Übersicht
  if (ws?.isCreator && !onboardingSteps(true).some((s) => s.key === schritt)) return <Navigate to="/agentur" replace />;
  return (
    <Rahmen ws={ws} account={account ?? null}>
      <SchrittSeite step={schritt} ws={ws} loggedIn={!!session} />
    </Rahmen>
  );
}

// ── Rahmen: Portal wie in den Vorlagen, vor dem Anlegen der Agentur mit schlanker Navigation ──

const SETUP_NAV: NavItem[] = [{ get label() { return t("Einrichtung", "Setup"); }, to: "/einrichtung/firma", icon: "settings", also: ["/einrichtung"] }];

/** Creator-Konto (0178): angelegt → Art des Kontos; noch nicht angelegt → Wahl aus der Registrierung */
function istCreatorEinrichtung(ws: StaffWorkspace | null): boolean {
  return ws ? !!ws.isCreator : storedKontoArt() === "creator";
}

function Rahmen({ ws, account, children }: { ws: StaffWorkspace | null; account: Account | null; children: ReactNode }) {
  // Texte mit eigener Creator-Fassung (ta) auch in der Einrichtung
  setCreatorKonto(istCreatorEinrichtung(ws));
  // Creator-Konto: dieselbe Navigation wie im Studio (Befund 06.10.2026: die Einrichtung zeigte Auszahlungen, Creator, Kennzahlen)
  const nav = ws ? (ws.isCreator ? studioNav() : agencyNav(true)) : SETUP_NAV;
  const bottom = ws ? (ws.isCreator ? studioBottom() : agencyBottom(true)) : SETUP_NAV;
  const name = ws?.name ?? account?.session.email ?? t("Nicht angemeldet", "Not signed in");
  return (
    <PortalLayout
      brandHref={ws ? "/agentur" : "/einrichtung"}
      orgName={ws?.org.name ?? (istCreatorEinrichtung(ws) ? t("Neues Creator-Konto", "New creator account") : t("Neue Agentur", "New agency"))}
      accent={ws?.org.brand_color}
      nav={nav}
      bottom={bottom}
      palette={[...onboardingSteps(istCreatorEinrichtung(ws)).map((s, i) => ({ label: `${i + 1}. ${s.label}`, to: `/einrichtung/${s.key}`, hint: t("Einrichtung", "Setup") })), ...(ws ? [{ label: t("Übersicht", "Overview"), to: "/agentur" }] : [])]}
      bell={ws ? { href: "/agentur/benachrichtigungen", unread: 0 } : undefined}
      account={account ? { name, initials: account.initials ?? initials(name), href: ws ? "/agentur/profil" : "/einrichtung/firma" } : { name, initials: "–", href: "/anmelden" }}
    >
      {children}
    </PortalLayout>
  );
}

function StepsNav({ current, creator = false }: { current: OnboardingStep; creator?: boolean }) {
  return (
    <nav className="steps" aria-label={t("Einrichtung", "Setup")}>
      {onboardingSteps(creator).map((s, i) => (
        <Link key={s.key} className={s.key === current ? "current" : ""} aria-current={s.key === current ? "page" : undefined} to={`/einrichtung/${s.key}`}>
          {i + 1}. {s.label}
        </Link>
      ))}
    </nav>
  );
}

/** Inhalt eines Schritts im Rahmen (auch für Tests ohne Sitzungsanbieter nutzbar). */
export function SchrittSeite({ step, ws, loggedIn, schlank = false }: { step: OnboardingStep; ws: StaffWorkspace | null; loggedIn: boolean; schlank?: boolean }) {
  const creator = istCreatorEinrichtung(ws);
  const steps = onboardingSteps(creator);
  const i = Math.max(0, steps.findIndex((s) => s.key === step));
  const label = steps[i]!.label;
  const total = steps.length;
  let content: ReactNode;
  if (step === "registrierung") content = <SchrittKonto loggedIn={loggedIn} hasAgency={!!ws} />;
  else if (!loggedIn) content = <Hinweis text={t("Bitte lege zuerst ein Konto an.", "Please create an account first.")} to="/einrichtung/registrierung" link={t("1. Konto anlegen", "1. Create account")} />;
  else if (!ws) content = step === "firma" ? <SchrittFirmaNeu /> : <Hinweis text={t("Lege zuerst deine Agentur mit den Firmendaten an.", "First create your agency with the company details.")} to="/einrichtung/firma" link={t("2. Firmendaten", "2. Company details")} />;
  else content = <MitAgentur step={step} ws={ws} />;
  // Im schlanken Rahmen (vor der Zahlung): nur der Inhalt, keine Schrittliste und kein Fortschritt
  if (schlank) {
    return (
      <div className="tk-start-inhalt stack">
        <h1>{loggedIn ? t("Fast fertig", "Almost done") : t("Konto anlegen", "Create account")}</h1>
        {content}
      </div>
    );
  }
  return (
    <main id="main" className="content">
      <PageHead eyebrow={creator ? t("Dein Creator-Konto", "Your creator account") : ta("Agentur-Arbeitsbereich", "Agency workspace", "Dein Creator-Konto", "Your creator account")} title={label} text={t(`Schritt ${i + 1} von ${total} · Später fortsetzbar`, `Step ${i + 1} of ${total} · can be continued later`)} />
      {ws && (
        <div className="notice row between" role="note">
          <span>{t("Du musst nicht alles sofort ausfüllen: Das Portal ist schon nutzbar. Was noch fehlt, zeigt dir die Übersicht, und du ergänzt es, wenn du es brauchst (zum Beispiel vor der ersten Rechnung).",
            "You do not have to fill in everything right away: the portal is already usable. The overview shows what is still missing, and you add it when you need it (for example before the first invoice).")}</span>
          <Link className="btn" to="/agentur">{t("Später ausfüllen und Portal öffnen", "Fill in later and open the portal")}</Link>
        </div>
      )}
      <StepsNav current={step} creator={creator} />
      <div className="progress" aria-label={t(`Schritt ${i + 1} von ${total}`, `Step ${i + 1} of ${total}`)}><span style={{ width: `${((i + 1) / total) * 100}%` }}></span></div>
      {content}
      {api.mode === "demo" && <p className="demo-note">{DEMO_NOTE()}</p>}
    </main>
  );
}

function Hinweis({ text, to, link }: { text: string; to: string; link: string }) {
  return (
    <div className="panel stack tk-empty">
      <p>{text}</p>
      <Link className="btn primary" to={to}>{link}</Link>
    </div>
  );
}

function Buttons({ busy, next = true, saveLabel = t("Zwischenspeichern", "Save for now") }: { busy: boolean; next?: boolean; saveLabel?: string }) {
  return (
    <>
      <button className="" type="submit" name="aktion" value="speichern" disabled={busy}>{busy ? t("Wird gespeichert …", "Saving …") : saveLabel}</button>
      {next && <button className="primary" type="submit" name="aktion" value="weiter" disabled={busy}>{t("Weiter", "Next")}</button>}
    </>
  );
}

// ── Schritt 1: Konto anlegen und Darstellung wählen ──────────────────────────────

/**
 * Erster, persönlicher Schritt: Konto anlegen und die Darstellung wählen (Dunkel vorausgewählt, wirkt sofort; 0121).
 * Die Registrierung (/registrieren) und der Link aus der Bestätigungsmail führen angemeldet hierher; gespeichert wird die
 * Darstellung mit dem Schritt (theme und theme_chosen_at), danach geht es zu den Firmendaten.
 */
function SchrittKonto({ loggedIn, hasAgency = true }: { loggedIn: boolean; hasAgency?: boolean }) {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [vorhanden, setVorhanden] = useState(false);
  // Wie Registrieren.tsx: AGB, AVV, Datenschutz und die eigene B2B-Bestätigung (Anwaltsvorgabe 06.10.2026), beide nicht vorangekreuzt
  const [accepted, setAccepted] = useState(false);
  const [b2b, setB2b] = useState(false);
  // Supabase Auth verlangt bei eingeschaltetem CAPTCHA ein Token (wie Registrieren.tsx; Befund 02.10.2026)
  const [captcha, setCaptcha] = useState<string | null>(null);
  const [captchaRunde, setCaptchaRunde] = useState(0);
  const mitCaptcha = api.mode !== "demo" && !!captchaConfig();
  const [darstellung, setDarstellung] = useDarstellungEntwurf(loggedIn ? api.session()?.userId ?? null : null);
  // Scheitert das Speichern im Konto, gilt die Wahl auf diesem Gerät; die Einrichtung geht trotzdem weiter
  const darstellungSpeichern = async () => {
    const userId = api.session()?.userId;
    if (userId && !(await saveThemeChoice(userId, darstellung))) toast(themeNotSavedText(), "alert");
  };
  // Nach Bestätigungslink oder Rückkehr von Google: vorgemerkte Zustimmung (AGB, Datenschutz) gleich eintragen; den AVV erst mit Agentur
  useEffect(() => {
    if (loggedIn) void settlePendingConsents().catch(() => undefined);
  }, [loggedIn]);
  // Inhaber 03.10.2026: erst bezahlen, dann einrichten. Ohne Agentur geht es nach dem Anmelden direkt zum Schnellstart
  // (Agentur mit dem Namen aus der Registrierung anlegen, dann Zahlung); Darstellung und Firmendaten folgen danach.
  // Prüf-Chat CP-001 (02.11.2026): offene Einladung an die Anmeldeadresse zuerst anbieten, sonst legt man versehentlich eine Agentur an
  if (loggedIn && !hasAgency) return <OffeneEinladungen><KontoAnlage /></OffeneEinladungen>;
  if (loggedIn) {
    return (
      <div className="panel stack" aria-busy={busy}>
        <p>{t("Du bist angemeldet als ", "You are signed in as ")}<strong>{api.session()?.email}</strong>{t(". Wähle noch, wie dein Portal aussehen soll; danach geht es mit den Firmendaten weiter.", ". Choose how your portal should look; next come the company details.")}</p>
        <DarstellungAbschnitt value={darstellung} onChange={setDarstellung} heading="h2" />
        <div className="row">
          <button className="primary" type="button" disabled={busy} onClick={() => {
            setBusy(true);
            void darstellungSpeichern().then(() => navigate("/einrichtung/firma"));
          }}>{t("Weiter", "Next")}</button>
        </div>
      </div>
    );
  }
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const mode = submitMode(e);
    const c = new Check();
    const mail = c.email("email", email, { label: t("die E-Mail-Adresse", "the email address"), required: true });
    if (api.mode !== "demo") c.text("password", password, { label: t("ein Passwort", "a password"), required: true, min: MIN_PASSWORD_LENGTH, max: MAX_PASSWORD_LENGTH });
    if (!accepted) c.fail("accepted", t("Bitte die Bedingungen akzeptieren.", "Please accept the terms."));
    if (!b2b) c.fail("b2b", b2bMissingText());
    if (mitCaptcha && !captcha) c.fail("captcha", t("Bitte die Sicherheitsprüfung lösen.", "Please complete the security check."));
    setErrors(c.errors);
    if (!c.ok || !mail) return;
    setBusy(true);
    setError(null);
    try {
      // Zustimmung und B2B-Bestätigung an diese Mailadresse vormerken; eingetragen nach der Bestätigung (settlePendingConsents)
      rememberPendingConsent(mail, ["agency_terms", "privacy", "dpa", "b2b_declaration"]);
      // Der Link aus der Bestätigungsmail führt angemeldet zurück zu diesem Schritt (Darstellung, dann Weiter)
      const vormerkung = { art: storedKontoArt(), name: storedAgencyName() || null, plan: recall(PLAN_KEY) };
      const r = await api.signUp(mail, password, { captchaToken: captcha, redirectTo: "/einrichtung/registrierung", language: currentLang(), vormerkung });
      if (r.needsConfirmation) setConfirm(true);
      else {
        await darstellungSpeichern();
        if (mode === "weiter") navigate("/einrichtung/firma");
      }
    } catch (err) {
      if (authFehlerArt(err) === "user_exists") setVorhanden(true);
      else setError(fehlerText(err));
    } finally {
      setBusy(false);
      if (mitCaptcha) setCaptchaRunde((n) => n + 1);
    }
  }
  if (vorhanden) return <KontoVorhanden email={email.trim()} onBack={() => { setVorhanden(false); setPassword(""); }} />;
  // Nach dem Absenden springt der Schritt um: Postfach prüfen (Inhaber 06.10.2026)
  // Nach dem Code ist die Person angemeldet; der Schritt zeigt dann die Kontoanlage und es geht zur Zahlung
  if (confirm) return <PostfachPruefen email={email.trim()} redirectTo="/einrichtung/registrierung" onBack={() => { setConfirm(false); setPassword(""); }} onVerified={() => setConfirm(false)} />;
  return (
    <form className="panel stack" onSubmit={(e) => void submit(e)} noValidate aria-busy={busy}>
      <TextField label={t("E-Mail", "Email")} type="email" value={email} onChange={setEmail} error={errors.email} required autoComplete="email" />
      <TextField label={t("Passwort", "Password")} type="password" value={password} onChange={setPassword} error={errors.password} autoComplete="new-password"
        hint={api.mode === "demo" ? t("In der Demo gibt es keine Passwörter; das Konto gilt bis zum Neuladen.", "There are no passwords in the demo; the account lasts until you reload.") : passwordHint(currentLang())} />
      <DarstellungAbschnitt value={darstellung} onChange={setDarstellung} heading="h2" />
      <RechtstexteZustimmung id="konto-agb" checked={accepted} onChange={setAccepted} error={errors.accepted} />
      <B2bBestaetigung id="konto-b2b" checked={b2b} onChange={setB2b} error={errors.b2b} />
      {mitCaptcha && <CaptchaFeld onToken={setCaptcha} resetKey={captchaRunde} />}
      <FormErrors errors={errors} error={error} />
      <Buttons busy={busy} />
    </form>
  );
}

/**
 * Kontoanlage gleich nach Code bzw. Bestätigungslink (Inhaber 06.10.2026: Zahlung so früh wie möglich). Was bei der Registrierung
 * gewählt wurde, steht im Konto (user_metadata.vormerkung) und gilt vor dem Browser-Speicher; ohne Vormerkung (z. B. Google)
 * bleibt der Browser-Speicher. Mehrere offene Tabs legten zwei Agenturen an (Befund 06.10.2026): Die Anlage sperrt sich darum
 * für eine Minute im Browser, die anderen Tabs warten und gehen dann ins fertige Konto.
 */
function KontoAnlage() {
  const userId = api.session()?.userId ?? null;
  const v = useQuery({ queryKey: ["konto-vormerkung", userId], queryFn: kontoVormerkungMitHerkunft, staleTime: Infinity });
  const [fremd] = useState(() => anlageLaeuft());
  useEffect(() => {
    if (!fremd) return;
    const id = window.setTimeout(() => window.location.assign("/agentur"), 5000);
    return () => window.clearTimeout(id);
  }, [fremd]);
  if (fremd) return <div className="panel stack" aria-busy="true"><p>{t("Dein Konto wird gerade in einem anderen Tab angelegt …", "Your account is being created in another tab …")}</p></div>;
  if (v.isPending) return <Loading />;
  const vm = v.data ?? {};
  if (typeof vm.kaufen === "boolean") rememberKauf(vm.kaufen);
  const creator = "art" in vm ? vm.art === "creator" : storedKontoArt() === "creator";
  const name = (vm.name ?? "").trim() || storedAgencyName();
  const plan = vm.plan ?? recall(PLAN_KEY);
  const segment = isCreatorSegment(vm.segment ?? null) ? vm.segment! : recall(SEGMENT_KEY);
  return creator ? <CreatorSchnellstart name={name} segment={segment} /> : <Schnellstart name={name} plan={plan} />;
}
/**
 * Vormerkung lesen; kam die Person über „Mit Google registrieren“, steht die Herkunft in der Rücksprung-Adresse (?von=&ref=&utm_…)
 * und wird hier einmal ins Konto übernommen, bevor die Agentur entsteht (Sofortmeldung und Konsole lesen sie aus user_metadata).
 * Hat das Konto schon eine Herkunft (Registrierung per E-Mail), bleibt sie unverändert.
 */
async function kontoVormerkungMitHerkunft() {
  const vm = await api.kontoVormerkung();
  const h = herkunftAus(new URLSearchParams(typeof window !== "undefined" ? window.location.search : ""));
  if (!hatHerkunft(h) || vm.von || vm.ref || vm.utm || !api.kontoHerkunftNachtragen) return vm;
  await api.kontoHerkunftNachtragen(h).catch(() => undefined);
  return { ...vm, ...h };
}
const ANLAGE_KEY = "tk-konto-anlage";
function anlageLaeuft(): boolean {
  try {
    const seit = Number(localStorage.getItem(ANLAGE_KEY) ?? 0);
    return seit > 0 && Date.now() - seit < 60_000;
  } catch {
    return false;
  }
}
function anlageSperren(on: boolean) {
  try {
    if (on) localStorage.setItem(ANLAGE_KEY, String(Date.now()));
    else localStorage.removeItem(ANLAGE_KEY);
  } catch {
    /* ohne Speicher: keine Sperre über Tabs */
  }
}

/**
 * Schnellstart (Inhaber 03.10.2026): die Agentur entsteht mit dem Namen aus der Registrierung und den Vorgaben des Landes
 * (aus der Browsersprache), der Tarif von der Preisseite gilt; danach direkt die Zahlung (Testphase mit Zahlungsmethode oder
 * sofort starten). Firmendaten, Steuer und Branding kommen nach der Zahlung in der Einrichtung.
 */
function Schnellstart({ name: vorgabe, plan }: { name: string; plan: string | null }) {
  const navigate = useNavigate();
  const switchOrg = useSwitchStaffOrg();
  const [name, setName] = useState(vorgabe);
  const [fehler, setFehler] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const gestartet = useRef(false);
  // Inhaber 14.10.2026: Das Land wird zu Beginn ausdrücklich gewählt (Vorschlag aus der Browsersprache). Es bestimmt Steuern,
  // Belege und Fristen und steht fest, sobald es Kooperationen, Rechnungen oder Verträge gibt (0265).
  const [land, setLand] = useState(() => {
    const l = (typeof navigator !== "undefined" ? navigator.language ?? "" : "").toUpperCase();
    return l.endsWith("-AT") ? "AT" : l.endsWith("-CH") ? "CH" : "DE";
  });
  const anlegen = async (n: string) => {
    setBusy(true);
    setFehler(null);
    try {
      anlageSperren(true);
      await settlePendingConsents().catch(() => undefined);
      const orgId = await createAgency(api, { name: n, country: land });
      // Google-Ads-Conversion „Registrierung“: nur mit Einwilligung, je Konto einmal (lib/einwilligung.ts)
      meldeRegistrierung(orgId);
      if (plan) await api.rpc("org_trial_choose_plan", { p_org: orgId, p_plan: plan }).catch(() => undefined);
      rememberTrialPlan(null);
      rememberAgencyName("");
      merkeEinrichtungNachZahlung(true);
      switchOrg(orgId);
      anlageSperren(false);
      // Zur Zahlung: das Portal zeigt ohne Zahlungsmethode zuerst den Abschluss
      navigate("/agentur", { replace: true });
    } catch (e) {
      anlageSperren(false);
      setFehler(fehlerText(e));
      setBusy(false);
    }
  };
  void gestartet;
  if (busy) return <div className="panel stack" aria-busy="true"><p>{t("Deine Agentur wird angelegt …", "Your agency is being created …")}</p></div>;
  return (
    <form className="panel stack" noValidate onSubmit={(e) => { e.preventDefault(); if (name.trim().length >= 2) void anlegen(name.trim()); else setFehler(t("Bitte den Namen deiner Agentur eintragen.", "Please enter your agency's name.")); }}>
      {/* Bauliste Vertrieb (Punkt 3): das einzige Feld zwischen Registrierung und Tarifwahl */}
      <TextField label={t("Wie heißt deine Agentur?", "What is your agency called?")} value={name} onChange={setName} autoComplete="organization" required
        hint={t("So steht sie später auf Verträgen und Rechnungen. Du kannst den Namen jederzeit ändern.", "This is how it later appears on contracts and invoices. You can change the name at any time.")} />
      <label>
        {t("Sitz der Agentur", "Agency's country")}
        <select value={land} onChange={(e) => setLand(e.target.value)} required>
          {AGENCY_COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
        </select>
        <small>{t("Danach richten sich Steuern, Rechnungen, Mahnungen, Verträge und Fristen. Sobald es Kooperationen, Rechnungen oder Verträge gibt, lässt sich das Land nur noch über den Support ändern.",
          "Taxes, invoices, dunning, contracts and deadlines follow it. Once there are deals, invoices or contracts, the country can only be changed via support.")}</small>
      </label>
      {fehler && <p className="tk-error" role="alert">{fehler}</p>}
      <div className="row"><button className="primary" type="submit">{t("Weiter zur Tarifwahl", "Continue to plan choice")}</button></div>
      {/* „Später bestätigen“ (Inhaber 21.10.2026): die Bestätigung der Adresse blockiert nichts (0247) */}
      <AdresseSpaeter />
    </form>
  );
}

/**
 * Schnellstart für selbständige Creator (0178): Name, Zielgruppe und Kleinunternehmer-Angabe, dann entsteht das Creator-Konto
 * (Tarif Creator) und es geht wie bei Agenturen zur Zahlung; Adresse, Steuer und Branding folgen in der Einrichtung.
 */
function CreatorSchnellstart({ name: gemerkt, segment: vorSegment }: { name: string; segment: string | null }) {
  const navigate = useNavigate();
  const switchOrg = useSwitchStaffOrg();
  const [first, setFirst] = useState(gemerkt.split(/\s+/)[0] ?? "");
  const [last, setLast] = useState(gemerkt.split(/\s+/).slice(1).join(" "));
  const [name, setName] = useState("");
  const [segment, setSegment] = useState<string>(isCreatorSegment(vorSegment) ? vorSegment! : "");
  const gestartet = useRef(false);
  const [klein, setKlein] = useState(true);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [fehler, setFehler] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const land = (() => {
    const l = (typeof navigator !== "undefined" ? navigator.language ?? "" : "").toUpperCase();
    return l.endsWith("-AT") ? "AT" : l.endsWith("-CH") ? "CH" : "DE";
  })();
  const [country, setCountry] = useState(land);
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const c = new Check();
    c.text("first", first, { label: t("deinen Vornamen", "your first name"), required: true, min: 1, max: 80 });
    c.text("last", last, { label: t("deinen Nachnamen", "your last name"), max: 80 });
    c.text("name", name, { label: t("einen Namen für dein Konto", "a name for your account"), max: 120 });
    setErrors(c.errors);
    if (!c.ok) return;
    await anlegen({ kontoName: name.trim() || `${first.trim()} ${last.trim()}`.trim(), vorname: first, nachname: last, seg: isCreatorSegment(segment) ? segment : null });
  }
  // Inhaber 06.10.2026: Zahlung zuerst. Ist der Name aus der Registrierung bekannt, entsteht das Konto ohne Formular und es geht
  // sofort zur Zahlung; Zielgruppe, Land und Steuerstatus folgen in der Einrichtung danach (Firmendaten).
  useEffect(() => {
    if (gestartet.current || gemerkt.trim().length < 2) return;
    gestartet.current = true;
    const teile = gemerkt.trim().split(/\s+/);
    void anlegen({ kontoName: gemerkt.trim(), vorname: teile[0]!, nachname: teile.slice(1).join(" "), seg: isCreatorSegment(vorSegment) ? vorSegment : null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  async function anlegen({ kontoName, vorname, nachname, seg }: { kontoName: string; vorname: string; nachname: string; seg: string | null }) {
    setBusy(true);
    setFehler(null);
    try {
      anlageSperren(true);
      await settlePendingConsents().catch(() => undefined);
      const orgId = await createCreatorAccount(api, { name: kontoName, country, segment: seg, firstName: vorname, lastName: nachname, smallBusiness: klein });
      meldeRegistrierung(orgId);
      rememberKontoArt(null);
      rememberZielgruppe(null);
      rememberAgencyName("");
      rememberTrialPlan(null);
      merkeEinrichtungNachZahlung(true);
      switchOrg(orgId);
      anlageSperren(false);
      navigate("/agentur", { replace: true });
    } catch (err) {
      anlageSperren(false);
      // Schon angelegt (anderer Tab, doppelter Klick): ins fertige Konto
      if ((err as { hint?: string } | null)?.hint === "creator:exists") { window.location.assign("/agentur"); return; }
      setFehler(fehlerText(err));
      setBusy(false);
    }
  }
  if (busy) return <div className="panel stack" aria-busy="true"><p>{t("Dein Creator-Konto wird angelegt …", "Your creator account is being created …")}</p></div>;
  return (
    <form className="panel stack" noValidate onSubmit={(e) => void submit(e)}>
      <p>{t("Dein eigenes Büro als Creator: Deals, Verträge, Rechnungen an Marken und Mahnungen an einem Ort. Ohne Agentur, ab 19 € netto im Monat (Creator) oder mit Anfragen, Media-Kits und Export für 29 € netto im Monat (Creator Pro), ", "Your own office as a creator: deals, contracts, invoices to brands and reminders in one place. No agency needed, from €19 net per month (Creator) or with inquiries, media kits and export for €29 net per month (Creator Pro), ")}{creatorNurUnternehmerText()}</p>
      <div className="tk-felder">
        <TextField label={t("Vorname", "First name")} value={first} onChange={setFirst} error={errors.first} required autoComplete="given-name" />
        <TextField label={t("Nachname", "Last name")} value={last} onChange={setLast} error={errors.last} autoComplete="family-name" />
      </div>
      <TextField label={t("Künstler- oder Firmenname (optional)", "Artist or business name (optional)")} value={name} onChange={setName} error={errors.name} autoComplete="organization"
        hint={t("Steht als Absender auf deinen Rechnungen. Leer: dein Name.", "Shown as the sender on your invoices. Empty: your name.")} />
      <label>
        {t("Was machst du hauptsächlich?", "What do you mainly do?")}
        <select value={segment} onChange={(e) => setSegment(e.target.value)}>
          <option value="">{t("Bitte wählen …", "Please choose …")}</option>
          {CREATOR_SEGMENTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
      </label>
      <label>
        {t("Land", "Country")}
        <select value={country} onChange={(e) => setCountry(e.target.value)}>
          {AGENCY_COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
        </select>
      </label>
      <label className="row tk-check">
        <input type="checkbox" checked={klein} onChange={(e) => setKlein(e.target.checked)} />
        <span>{smallBusinessLabel(country)}{t(" (ohne Umsatzsteuer auf deinen Rechnungen; später in den Einstellungen änderbar)", " (no VAT on your invoices; can be changed later in the settings)")}</span>
      </label>
      {fehler && <p className="tk-error" role="alert">{fehler}</p>}
      <div className="row"><button className="primary" type="submit">{t("Weiter zur Zahlung", "Continue to payment")}</button></div>
    </form>
  );
}

// ── Schritt 2: Firmendaten, Agentur anlegen ────────────────────────────────────

function SchrittFirmaNeu() {
  const navigate = useNavigate();
  const switchOrg = useSwitchStaffOrg();
  const { busy, error, run } = useAction();
  const trial = useTrialInfo();
  const chosen = recall(PLAN_KEY);
  const [solo, setSolo] = useState<string | null>(storedSolo());
  const initial = soloDraft(firmaDraftFrom({}, storedAgencyName()), solo);
  return (
    <>
      <div className="notice">
        {t("Mit dem ersten Speichern legst du deine Agentur an: ", "Saving for the first time creates your agency: ")}{trialText(trial.data, chosen)}{t(", du wirst Admin und Inhaber. Den Testtarif wechselst du jederzeit unter Einstellungen · Abo & Tarif.", ", you become admin and owner. You can change the trial plan at any time under Settings · Subscription & plan.")}</div>
      <AlleinPanel name={solo} onChange={setSolo} />
      <FirmaForm
        key={solo ?? "team"}
        initial={initial}
        multi={false}
        withOrgName
        strict={() => false}
        busy={!!busy}
        error={error}
        buttons={<Buttons busy={!!busy} />}
        onSubmit={(v, mode) =>
          // Anlegen und Firmendaten in einem Schritt: scheitert etwas, entsteht keine (zweite) Agentur
          void run("anlegen", async () => {
            let orgId: string;
            try {
              orgId = await createAgencyWithData(api, { name: v.orgName!, country: v.settings.country, settings: v.settings, legal: v.legal });
            } catch (e) {
              // 0205: Dasselbe Konto wurde gerade schon angelegt (zweiter Klick, zweiter Reiter, langsame Verbindung). Kein Fehler
              // für den Kunden: die Seite neu laden, dann geht es im vorhandenen Konto weiter.
              if ((e as { hint?: string } | null)?.hint === "org:duplicate") {
                window.location.assign("/einrichtung/steuer");
                return null;
              }
              throw e;
            }
            // Tarif von der Preisseite: in der Testphase ohne Rückfrage wählbar
            if (chosen && chosen !== trial.data?.planKey) await api.rpc("org_trial_choose_plan", { p_org: orgId, p_plan: chosen }).catch(() => undefined);
            if (solo) await saveMemberSelf(api, orgId, solo, "Inhaber").catch(() => undefined);
            rememberTrialPlan(null);
            meldeRegistrierung(orgId);
            return orgId;
          }, t("Agentur angelegt.", "Agency created.")).then((orgId) => {
            if (!orgId) return;
            switchOrg(orgId);
            navigate(mode === "weiter" ? "/einrichtung/steuer" : "/einrichtung/firma");
          })
        }
      />
    </>
  );
}

/** Schnellwahl „Ich führe die Agentur allein“: Vertretung aus dem eigenen Namen, später eine schlanke Mahnleiter. */
function soloDraft<T extends { representative_name: string; representative_position: string; legal_form: string }>(d: T, name: string | null): T {
  if (!name) return d;
  return {
    ...d,
    representative_name: d.representative_name || name,
    representative_position: d.representative_position || "Inhaber",
    legal_form: d.legal_form || "Einzelunternehmen",
  };
}

function AlleinPanel({ name, onChange }: { name: string | null; onChange: (v: string | null) => void }) {
  const [draft, setDraft] = useState(name ?? "");
  const [error, setError] = useState<string | undefined>();
  if (name) {
    return (
      <div className="notice stack" role="status">
        <span><strong>{t("Ich führe die Agentur allein:", "I run the agency alone:")}</strong> {name} {t("vertritt die Agentur als Inhaber. Die Mahnleiter schlagen wir schlank vor (eine Zahlungserinnerung); alles Weitere geht später.", "represents the agency as owner. We suggest a lean dunning process (one payment reminder); everything else can follow later.")}</span>
        <div className="row"><button type="button" onClick={() => { rememberSolo(null); onChange(null); }}>{t("Schnellwahl aufheben", "Undo quick choice")}</button></div>
      </div>
    );
  }
  return (
    <details className="panel stack">
      <summary>{t("Ich führe die Agentur allein", "I run the agency alone")}</summary>
      <p>{t("Trage deinen Namen ein: Er erscheint als Vertretung der Agentur (Inhaber) und als dein Name im Team. Die Mahnleiter schlagen wir dann mit einer Zahlungserinnerung vor.", "Enter your name: it appears as the agency's representative (owner) and as your name in the team. We will then suggest the dunning levels with one payment reminder.")}</p>
      <TextField label={t("Dein Name", "Your name")} value={draft} onChange={setDraft} error={error} autoComplete="name" />
      <div className="row">
        <button type="button" onClick={() => {
          const v = draft.trim();
          if (v.length < 2) return setError(t("Bitte deinen vollständigen Namen eintragen.", "Please enter your full name."));
          setError(undefined);
          rememberSolo(v);
          onChange(v);
        }}>{t("Übernehmen", "Take over")}</button>
      </div>
    </details>
  );
}

// ── Schritte mit angelegter Agentur ────────────────────────────────────────────

function MitAgentur({ step, ws }: { step: OnboardingStep; ws: StaffWorkspace }) {
  const orgId = ws.org.org_id;
  const q = useQuery({ queryKey: ["org_settings", orgId, "einrichtung"], queryFn: () => loadEinrichtung(api, orgId) });
  if (q.error) return <FormErrors errors={{}} error={fehlerText(q.error)} />;
  if (q.isPending) return <p className="muted tk-loading">{t("Wird geladen …", "Loading …")}</p>;
  const d = q.data;
  const missing = missingForStep(d.setup, step);
  return (
    <>
      {missing.length > 0 && <div className="notice" role="status">{t("Für diesen Schritt fehlt noch: ", "Still missing for this step: ")}{listText(missing)}.</div>}
      {step === "firma" && <SchrittFirma key={`${d.settings.updated_at}|${d.legal?.updated_at ?? ""}`} ws={ws} data={d} />}
      {step === "steuer" && <SchrittSteuer key={d.settings.updated_at} ws={ws} data={d} />}
      {step === "branding" && <SchrittBranding key={d.settings.updated_at} ws={ws} data={d} />}
      {step === "konditionen" && <SchrittKonditionen key={`${d.settings.updated_at}|${d.steps.length}`} ws={ws} data={d} />}
      {step === "vorlagen" && <SchrittVorlagen data={d} />}
      {step === "creator" && <SchrittCreator ws={ws} data={d} />}
    </>
  );
}

function useWeiter(step: OnboardingStep, creator = false) {
  const navigate = useNavigate();
  return (mode: Mode) => {
    const n = nextStep(step, creator);
    if (mode === "weiter") navigate(n ? `/einrichtung/${n}` : "/agentur");
  };
}

function SchrittFirma({ ws, data }: { ws: StaffWorkspace; data: EinrichtungData }) {
  const { busy, error, run } = useAction();
  const weiter = useWeiter("firma", !!ws.isCreator);
  const [solo, setSolo] = useState<string | null>(storedSolo());
  // 0266: Konten aus der Zeit vor der Landwahl im Schnellstart bekamen still „Deutschland“; sie wählen hier einmal ihr Land
  const landOffen = !data.settings.country_confirmed_at;
  const landFest = useQuery({ queryKey: ["org_country_locked", ws.org.org_id], queryFn: () => api.read<boolean>("org_country_locked", { p_org: ws.org.org_id }).catch(() => false) });
  const entwurf = soloDraft(firmaDraftFrom(data), solo);
  return (
    <>
    {landOffen && (
      <div className="notice" role="status">
        <strong>{ws.isCreator ? t("In welchem Land bist du ansässig?", "Which country are you based in?") : t("In welchem Land hat deine Agentur ihren Sitz?", "Which country is your agency based in?")}</strong>{" "}
        {t("Bitte wähle unten das Land. Danach richten sich Steuern, Rechnungen, Mahnungen, Verträge, Fristen und Formate; das Portal stellt alles selbst ein.",
          "Please choose the country below. Taxes, invoices, dunning, contracts, deadlines and formats follow it; the portal sets everything up itself.")}
      </div>
    )}
    {!data.legal?.representative_name && !ws.isCreator && <AlleinPanel name={solo} onChange={setSolo} />}
    {ws.isCreator && <ZielgruppePanel orgId={ws.org.org_id} />}
    <FirmaForm
      key={solo ?? "team"}
      initial={landOffen ? { ...entwurf, country: "" } : entwurf}
      countryLocked={landFest.data === true}
      withOrgName
      creator={!!ws.isCreator}
      slug={data.org.slug}
      strict={() => false}
      busy={!!busy}
      error={error}
      replyTo={data.settings.reply_to_email}
      buttons={<Buttons busy={!!busy} />}
      onSubmit={(v, mode) =>
        void run("firma", async () => {
          // In der Einrichtung folgt dem Land auch Zahlenformat und Hauswährung (die Agentur ist neu, es gibt noch keine Beträge)
          const land = v.settings.country !== data.settings.country ? countryDefaults(v.settings.country) : null;
          const settings = land
            ? { ...v.settings, locale: land.locale, currency: land.currency, currencies: [...new Set([land.currency, ...(data.settings.currencies ?? [])])] }
            : v.settings;
          await saveFirma(api, ws.org.org_id, { orgName: v.orgName && v.orgName !== data.org.name ? v.orgName : undefined, settings, legal: v.legal });
          if (solo && !ws.membership.display_name) await saveMemberSelf(api, ws.org.org_id, solo, "Inhaber").catch(() => undefined);
          return true;
        }, t("Firmendaten gespeichert.", "Company details saved.")).then((ok) => ok && weiter(mode))
      }
    />
    </>
  );
}

/**
 * Creator-Konto (Inhaber 06.10.2026): Die Zielgruppe wird erst nach der Zahlung gefragt, hier bei den Firmendaten. Sie steuert
 * nur die Startbelegung (org_settings.creator_segment) und speichert sich mit der Auswahl.
 */
function ZielgruppePanel({ orgId }: { orgId: string }) {
  const q = useQuery({
    queryKey: ["org_settings", orgId, "creator_segment"],
    queryFn: () => api.select<{ creator_segment: string | null }>("org_settings", { columns: "creator_segment", filters: [["org_id", "eq", orgId]] }).then((r) => r[0]?.creator_segment ?? ""),
  });
  const [wert, setWert] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const aktuell = wert ?? q.data ?? "";
  return (
    <section className="panel stack" aria-labelledby="zielgruppe-h">
      <h2 id="zielgruppe-h">{t("Was machst du hauptsächlich?", "What do you mainly do?")}</h2>
      <label>
        {t("Schwerpunkt", "Focus")}
        <select value={aktuell} disabled={q.isPending} onChange={(e) => {
          const v = e.target.value;
          setWert(v);
          setFehler(null);
          void api.update("org_settings", [["org_id", "eq", orgId]], { creator_segment: isCreatorSegment(v) ? v : null })
            .then(() => toast(t("Schwerpunkt gespeichert.", "Focus saved.")))
            .catch((err: unknown) => setFehler(fehlerText(err)));
        }}>
          <option value="">{t("Bitte wählen …", "Please choose …")}</option>
          {CREATOR_SEGMENTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <small>{t("Damit passen wir Vorlagen und Beispiele an; alle Funktionen hast du so oder so.", "We use this to tailor templates and examples; you get all features either way.")}</small>
      </label>
      {fehler && <p className="tk-error" role="alert">{fehler}</p>}
    </section>
  );
}

function SchrittSteuer({ ws, data }: { ws: StaffWorkspace; data: EinrichtungData }) {
  const navigate = useNavigate();
  const s = data.settings;
  const tax = s.small_business ? smallBusinessLabel(s.country) : s.country === "CH" || s.country === "LI" ? t("MWST-pflichtig", "Subject to VAT") : t("Regelbesteuerung", "Standard taxation");
  return (
    <>
      <section className="panel stack" aria-labelledby="steuer-title">
        <h3 id="steuer-title">{t("Steuerstatus", "Tax status")}</h3>
        <p>
          {AGENCY_COUNTRIES.find((c) => c.code === s.country)?.name ?? s.country ?? t("Land fehlt", "Country missing")} · {tax}
          {/* Bezeichnung wie Schritt 2 (PRAXIS-R3-7): USt-IdNr., UID-Nummer, MWST-Nummer (UID) */}
          {s.vat_id ? ` · ${countryDefaults(s.country).vatLabel} ${s.vat_id}` : ""}{s.tax_number ? t(` · Steuernummer ${s.tax_number}`, ` · tax number ${s.tax_number}`) : ""}
        </p>
        <small>{t("Die Steuerangaben erfasst du einmal bei den Firmendaten; der Steuerhinweis auf Rechnungen folgt ihnen. Sonderfälle bitte mit der Steuerberatung klären.", "You enter the tax details once in the company details; the tax notice on invoices follows them. Please clarify special cases with your tax advisor.")} {steuerVerantwortung()}</small>
        <div className="row"><Link className="btn" to="/einrichtung/firma">{t("Steuerangaben ändern", "Change tax details")}</Link></div>
      </section>
      <div className="notice" role="note">{t("Die Bankverbindung kannst du überspringen. Du brauchst sie erst, wenn du die erste Rechnung festschreibst; bis dahin erinnert dich die Übersicht daran.",
        "You can skip the bank details. You only need them when you finalise your first invoice; until then the overview reminds you.")}</div>
      <BankPanel orgId={ws.org.org_id} banks={data.banks} holder={s.legal_name ?? data.org.name} currency={s.currency} country={s.country} />
      <div className="row">
        <button className="primary" type="button" onClick={() => navigate("/einrichtung/branding")}>{data.banks.length ? t("Weiter", "Next") : t("Überspringen und weiter", "Skip and continue")}</button>
      </div>
    </>
  );
}

function SchrittBranding({ ws, data }: { ws: StaffWorkspace; data: EinrichtungData }) {
  const { busy, error, run } = useAction();
  const weiter = useWeiter("branding", !!ws.isCreator);
  return (
    <BrandingForm
      initial={brandingDraftFrom(data.settings, data.defaults, { orgName: data.org.name, email: api.session()?.email ?? "" })}
      orgName={data.org.name}
      orgId={ws.org.org_id}
      logoPath={data.settings.logo_path}
      strict={(mode) => mode === "weiter"}
      busy={!!busy}
      error={error}
      buttons={<Buttons busy={!!busy} />}
      onSubmit={(v, mode) => void run("branding", async () => {
        await updateSettings(api, ws.org.org_id, v);
        return true;
      }, t("Branding gespeichert.", "Branding saved.")).then((ok) => ok && weiter(mode))}
    />
  );
}

function SchrittKonditionen({ ws, data }: { ws: StaffWorkspace; data: EinrichtungData }) {
  const { busy, error, run } = useAction();
  // Tarifstaffel (0099): ohne eigene Mahnleiter keine Gebühren je Stufe; Stufen und Fristen gehen in jedem Tarif
  const feesLocked = useModul("module_dunning_custom", ws.org.org_id).gesperrt;
  const weiter = useWeiter("konditionen");
  const [steps, setSteps] = useState<StepDraft[]>(data.steps.length ? stepDrafts(data.steps) : storedSolo() ? slimLadder() : sampleLadder());
  const [ladderErrors, setLadderErrors] = useState<Record<string, string>>({});
  const direct = data.settings.invoice_route === "direct_to_brand";
  return (
    <KonditionenForm
      settings={data.settings}
      defaults={data.defaults}
      steps={data.steps}
      collapsed
      strict={(mode) => mode === "weiter"}
      busy={!!busy}
      error={error}
      buttons={<Buttons busy={!!busy} />}
      extra={
        <>
          <h2>{t("Mahnleiter", "Dunning levels")}</h2>
          <p>
            {direct
              ? t("Rechnungen gehen direkt vom Creator an die Marke; die Mahnleiter gilt hier für eigene Rechnungen der Agentur, etwa Provisionsrechnungen an Creator. ", "Invoices go directly from the creator to the brand; the dunning levels here apply to the agency's own invoices, such as commission invoices to creators. ")
              : ""}
            {t("Die Muster-Leiter hat vier Stufen ohne Fristen und Gebühren. Trage je Stufe die Zahlungsfrist ein; Gebühren, Zinsen und Texte lassen sich später unter Einstellungen ändern.", "The sample process has four levels without deadlines or fees. Enter the payment deadline per level; fees, interest and texts can be changed later under Settings.")}</p>
          <div className="row">
            <button type="button" onClick={() => setSteps(slimLadder())}>{t("Schlanke Leiter: nur eine Zahlungserinnerung", "Lean process: one payment reminder only")}</button>
            <button type="button" onClick={() => setSteps(sampleLadder())}>{t("Muster mit vier Stufen", "Sample with four levels")}</button>
          </div>
          {feesLocked && <TarifHinweis modul="module_dunning_custom" id="einrichtung-mahn-tarif" was={t("Eigene Gebühren je Stufe", "Custom fees per level")} />}
          <LadderEditor drafts={steps} onChange={setSteps} errors={ladderErrors} inUse={{}} currency={data.settings.currency} feesLocked={feesLocked} feeHintId={feesLocked ? "einrichtung-mahn-tarif" : undefined} />
        </>
      }
      onSubmit={(v, mode) => {
        const ladder = validateLadder(steps);
        setLadderErrors(mode === "weiter" ? ladder.errors : {});
        if (mode === "weiter" && Object.keys(ladder.errors).length) return;
        const saveSteps = !Object.keys(ladder.errors).length;
        void run("konditionen", async () => {
          await updateSettings(api, ws.org.org_id, v);
          if (saveSteps) await saveLadder(api, ws.org.org_id, ladder.rows, data.steps, {});
          return true;
        }, saveSteps ? t("Konditionen und Mahnleiter gespeichert.", "Terms and dunning levels saved.") : t("Konditionen gespeichert. Die Mahnleiter ist noch unvollständig.", "Terms saved. The dunning levels are still incomplete.")).then((ok) => ok && weiter(mode));
      }}
    />
  );
}

function SchrittVorlagen({ data }: { data: EinrichtungData }) {
  const navigate = useNavigate();
  const lang = data.settings.default_document_language ?? "de";
  // Vorlagen in der Belegsprache der Agentur zuerst, dann nach Name (wie auf der Einstellungsseite, Befund 24)
  const templates = sortTemplates(data.templates, lang);
  const [templateId, setTemplateId] = useState(templates[0]?.id ?? "");
  const keys = [...new Set(data.platformTexts.map((t) => t.key))]
    .filter((k) => (textGroup(k) === "email" || textGroup(k) === "dunning") && !isHiddenText(k))
    .sort((a, b) => textRank(a) - textRank(b) || textLabel(a).localeCompare(textLabel(b), "de"));
  const [key, setKey] = useState(keys.includes("email.contract_to_sign") ? "email.contract_to_sign" : keys[0] ?? "");
  const own = data.orgTexts.find((t) => t.key === key && t.language === lang);
  const base = data.platformTexts.find((t) => t.key === key && t.language === lang);
  const template = templates.find((t) => t.id === templateId);
  return (
    <form className="panel stack" onSubmit={(e) => { e.preventDefault(); navigate("/einrichtung/creator"); }}>
      <label>
        {t("Vertragsvorlage", "Contract template")}<select value={templateId} onChange={(e) => setTemplateId(e.target.value)} disabled={!templates.length}>
          {templates.length === 0 && <option value="">{t("Noch keine Vertragsvorlage", "No contract template yet")}</option>}
          {templates.map((t) => <option key={t.id} value={t.id}>{t.name}{t.language !== lang ? ` (${sprachName(t.language)})` : ""}</option>)}
        </select>
      </label>
      {template && <small>{sprachName(template.language)} {t("· Version ", "· version ")}{template.version} {t("· Muster der Plattform. Vor dem ersten Einsatz rechtlich prüfen lassen.", "· platform sample. Have it legally reviewed before first use.")}</small>}
      <label>
        {t("E-Mail-Vorlage", "Email template")}<select value={key} onChange={(e) => setKey(e.target.value)}>
          {keys.map((k) => <option key={k} value={k}>{textLabel(k)}</option>)}
        </select>
      </label>
      {(own ?? base) && (
        <div className="notice">
          <strong>{own?.subject ?? base?.subject}</strong>
          <p>{(own?.body ?? base?.body ?? "").replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().slice(0, 280)} …</p>
          <small>{own ? t("Eigene Fassung der Agentur", "Agency's own version") : t("Standardtext der Plattform", "Platform default text")}</small>
        </div>
      )}
      <Link className="btn " to={template ? `/agentur/vertraege/vorlagen/${template.id}` : "/agentur/vertraege/vorlagen"}>{t("Vorlage bearbeiten", "Edit template")}</Link>
      <Link className="btn " to="/agentur/einstellungen/vorlagen">{t("Texte anpassen", "Adjust texts")}</Link>
      <p>{t("Vorlagen sind vorbereitet und nicht Pflicht; Anpassungen gehen auch später.", "Templates are prepared and not mandatory; adjustments can also be made later.")}</p>
      <button className="primary" type="submit">{t("Weiter", "Next")}</button>
    </form>
  );
}

function SchrittCreator({ ws, data }: { ws: StaffWorkspace; data: EinrichtungData }) {
  const { busy, error, run } = useAction();
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [email, setEmail] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [done, setDone] = useState(false);
  const open = data.setup.missing;
  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const c = new Check();
    const first_name = c.text("first", first, { label: t("den Vornamen", "the first name"), required: true, max: 80 });
    const last_name = c.text("last", last, { label: t("Nachname", "Last name"), max: 80 });
    const mail = c.email("email", email, { label: t("die E-Mail-Adresse", "the email address"), required: true });
    setErrors(c.errors);
    if (!c.ok || !first_name || !mail) return;
    void run("creator", () => inviteCreator(api, ws.org.org_id, { first_name, last_name, email: mail }),
      api.mode === "demo" ? t("Creator angelegt und eingeladen. In der Demo wird keine Mail verschickt.", "Creator created and invited. In the demo no email is sent.") : t("Creator angelegt und eingeladen.", "Creator created and invited."))
      .then((r) => {
        if (!r) return;
        setFirst("");
        setLast("");
        setEmail("");
        setDone(true);
      });
  }
  return (
    <>
      <form className="panel stack" onSubmit={submit} noValidate aria-busy={!!busy}>
        <div className="grid">
          <TextField label={t("Vorname", "First name")} value={first} onChange={setFirst} error={errors.first} />
          <TextField label={t("Nachname", "Last name")} value={last} onChange={setLast} error={errors.last} />
        </div>
        <TextField label={t("Creator E-Mail", "Creator email")} type="email" value={email} onChange={setEmail} error={errors.email} required />
        <p>{t("Der Creator bekommt einen Einladungslink per Mail und legt damit sein Konto an. Die Einladung lässt sich unter Creator erneut senden oder zurückziehen.", "The creator receives an invitation link by email and uses it to create their account. The invitation can be resent or withdrawn under Creators.")}</p>
        <p>{t("Viele Creator oder Marken aus Excel oder einem anderen Werkzeug? ", "Many creators or brands from Excel or another tool? ")}<Link to="/agentur/creator/import">{t("Creator importieren", "Import creators")}</Link> · <Link to="/agentur/marken/import">{t("Marken importieren", "Import brands")}</Link> {t("· mehr unter „Bestand übernehmen“.", "· more under “Take over existing data”.")}</p>
        <FormErrors errors={errors} error={error} />
        <div className="row">
          <button className="" type="submit" disabled={!!busy}>{busy ? t("Wird erstellt …", "Creating …") : t("Einladung erstellen", "Create invitation")}</button>
          <Link className="btn primary" to="/agentur">{t("Zum Dashboard", "Go to dashboard")}</Link>
        </div>
        {done && <small role="status">{t("Weitere Creator lädst du hier oder später unter Creator ein.", "You can invite more creators here or later under Creators.")}</small>}
      </form>
      <section className="panel stack" aria-labelledby="bestand-title">
        <h3 id="bestand-title">{t("Bestand übernehmen", "Take over existing data")}</h3>
        <p>
          {t("Umstieg aus einem anderen System? Übernimm den Bestand in dieser Reihenfolge: Creator, Marken, laufende Kooperationen mit dem Stand vergangener Monate und offene Rechnungen, auch schon gemahnte und noch auszuzahlende. Dabei gehen keine Mails raus und niemand bekommt eine Glocke; Mahnungen und Auszahlungen laufen danach hier weiter.", "Switching from another system? Take over the existing data in this order: creators, brands, ongoing deals with the status of past months and open invoices, including those already dunned and still to be paid out. No emails go out and nobody gets a notification; dunning and payouts then continue here.")}</p>
        <div className="row">
          <Link className="btn" to="/agentur/creator/import">{t("1. Creator", "1. Creators")}</Link>
          <Link className="btn" to="/agentur/marken/import">{t("2. Marken", "2. Brands")}</Link>
          <Link className="btn" to="/agentur/kooperationen/import">{t("3. Kooperationen", "3. Deals")}</Link>
          <Link className="btn" to="/agentur/rechnungen/import">{t("4. Offene Rechnungen", "4. Open invoices")}</Link>
        </div>
      </section>
      {data.talents.length > 0 && (
        <section className="panel stack" aria-labelledby="eingeladen-title">
          <h3 id="eingeladen-title">{t("Creator dieser Agentur", "Creators of this agency")}</h3>
          {data.talents.map((tl) => (
            <div key={tl.id} className="task">
              <div><strong>{talentName(tl)}</strong><p>{tl.email ?? t("ohne E-Mail", "without email")}</p></div>
              {tl.status === "invited" ? <span className="pill warning"><span className="pill-zeichen">◷</span> {t("Eingeladen", "Invited")}</span> : tl.status === "active" ? <span className="pill success"><span className="pill-zeichen">✓</span> {t("Aktiv", "Active")}</span> : <span className="pill"><span className="pill-zeichen">○</span> {t("Archiviert", "Archived")}</span>}
            </div>
          ))}
        </section>
      )}
      <section className="panel stack" aria-labelledby="weiter-title">
        <h3 id="weiter-title">{t("Danach", "Afterwards")}</h3>
        <p>
          {t("Kolleginnen und Kollegen lädst du unter ", "You invite colleagues under ")}<Link to="/agentur/einstellungen/team">{t("Team & Rechte", "Team & permissions")}</Link> {t("ein (mit Namen und Rechten). Gebühren und Verzugszinsen der Mahnleiter legst du unter ", "(with names and permissions). You set the fees and default interest of the dunning levels under ")}<Link to="/agentur/einstellungen/mahnleiter">{t("Mahnleiter", "Dunning levels")}</Link> {t("fest.", ".")}</p>
      </section>
      <section className="panel stack" aria-labelledby="stand-title">
        <h3 id="stand-title">{t("Stand der Einrichtung", "Setup status")}</h3>
        {open.length === 0 ? (
          <p><span className="pill success"><span className="pill-zeichen">✓</span> {t("Vollständig", "Complete")}</span> {t("Alle Pflichtangaben sind eingetragen.", "All required details have been entered.")}</p>
        ) : (
          <>
            <p><span className="pill warning"><span className="pill-zeichen">◷</span> {t("Offen", "Open")}</span> {t("Das Portal ist nutzbar. Für Rechnungen und Verträge brauchst du später noch:", "The portal is usable. For invoices and contracts you will still need:")}</p>
            {open.map((m) => {
              const item = SETUP_ITEMS[m.key];
              return (
                <div key={m.key} className="task">
                  <div><strong>{item?.label ?? m.key}</strong></div>
                  <Link className="btn" to={`/einrichtung/${item?.step ?? "firma"}`}>{t("Zum Schritt", "Go to step")}</Link>
                </div>
              );
            })}
          </>
        )}
      </section>
    </>
  );
}

